import { defaultLanguage } from 'cf-i18n/constants'
import { isStudioUrlLocaleSegment, studioLanguageToUrlSegment } from 'cf-i18n/helpers'

/** Strips an optional Studio locale prefix from the first path segment. */
function stripLocalePrefix(segments: string[]): string[] {
  if (segments.length > 0 && isStudioUrlLocaleSegment(segments[0]!)) {
    return segments.slice(1)
  }

  return segments
}

/**
 * Removes the locale prefix from a pathname, so it can be compared against the
 * unprefixed `ROUTES` constants and route configs. `/it/flow` becomes `/flow`;
 * a pathname without a prefix is returned unchanged.
 */
export function stripStudioLocalePrefix(pathname: string): string {
  const segments = stripLocalePrefix(pathname.split('/').filter(Boolean))

  if (segments.length === 0) {
    return '/'
  }

  return `/${segments.join('/')}`
}

/**
 * Rewrites a pathname so its locale prefix matches the given account/cookie
 * language. English (the default) is unprefixed; other locales use
 * `studioLanguageToUrlSegment` (e.g. `zh_Hant` → `zh-tw`).
 */
export function canonicalizeStudioLocalePathname(pathname: string, language: string): string {
  const segments = stripLocalePrefix(pathname.split('/').filter(Boolean))
  const urlLang = studioLanguageToUrlSegment(language)
  const defaultUrlSegment = studioLanguageToUrlSegment(defaultLanguage)

  if (urlLang !== defaultUrlSegment) {
    segments.unshift(urlLang)
  }

  if (segments.length === 0) {
    return '/'
  }

  return `/${segments.join('/')}`
}

/** Normalizes pathnames for equality checks (trailing slashes ignored). */
export function normalizePathnameForLocaleCompare(pathname: string): string {
  const trimmed = pathname.replace(/\/+$/, '')
  return trimmed === '' ? '/' : trimmed
}

export function isCanonicalStudioLocalePathname(pathname: string, language: string): boolean {
  const canonical = canonicalizeStudioLocalePathname(pathname, language)
  return (
    normalizePathnameForLocaleCompare(pathname) === normalizePathnameForLocaleCompare(canonical)
  )
}
